import React, { useState, useEffect } from 'react';
import { elementsAPI } from '../services/api';
import { Element } from '../types';
import ElementCard from '../components/ElementCard';
// @ts-ignore
import CreateElementModal from '../components/CreateElementModal';
import ElementSidePanel from '../components/ElementSidePanel';
import ElementPreviewPanel from '../components/ElementPreviewPanel';
import '../styles/Elements.css';

console.log('🚨🚨🚨 ELEMENTS.TSX FILE LOADED 🚨🚨🚨');

const Elements: React.FC = () => {
  console.log('🚨🚨🚨 ELEMENTS COMPONENT MOUNTED - DEBUG VERSION 🚨🚨🚨');
  
  const [elements, setElements] = useState<Element[]>([]);
  const [filteredElements, setFilteredElements] = useState<Element[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest'>('date-created');
  
  // Global tab selection state
  const [globalTabSelection, setGlobalTabSelection] = useState<string>('summary');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showElementPanel, setShowElementPanel] = useState(false);
  const [showElementPreview, setShowElementPreview] = useState(false);
  const [selectedElement, setSelectedElement] = useState<Element | null>(null);
  const [updatedElementIds, setUpdatedElementIds] = useState<Set<string>>(new Set());
  const [dataVersion, setDataVersion] = useState(0); // Add version to force remounts

  // Debug: Log initial state
  console.log('🔍 Elements: Initial state:', {
    elementsLength: elements.length,
    filteredElementsLength: filteredElements.length,
    loading,
    dataVersion
  });

  useEffect(() => {
    console.log('🔍 Elements: useEffect for loadElements called');
    loadElements();
  }, []);

  // Monitor elements array changes for debugging (only log significant changes)
  useEffect(() => {
    console.log('🔍 Elements: elements array changed:', {
      elementsLength: elements.length,
      loading,
      dataVersion
    });
    
    if (elements.length > 0) {
      console.log('🔄 Elements: Elements loaded:', {
        total: elements.length,
        withImageUrl: elements.filter(e => e.imageUrl).length
      });
      
      // Check if any elements have related character data
      const elementsWithRelatedChars = elements.filter(e => 
        (e.weaponsOf && e.weaponsOf.length > 0) || 
        (e.armorOf && e.armorOf.length > 0) || 
        (e.itemsOf && e.itemsOf.length > 0) || 
        (e.speciesOf && e.speciesOf.length > 0) || 
        (e.genderOf && e.genderOf.length > 0) || 
        (e.beliefsOf && e.beliefsOf.length > 0) || 
        (e.familyOf && e.familyOf.length > 0) || 
        (e.affiliationsOf && e.affiliationsOf.length > 0) || 
        (e.abilitiesOf && e.abilitiesOf.length > 0)
      );
      
      console.log('🔍 Elements with related characters:', elementsWithRelatedChars.length);
      
      if (elementsWithRelatedChars.length > 0) {
        console.log('🔍 Sample element with related chars:', {
          name: elementsWithRelatedChars[0].name,
          weaponsOf: elementsWithRelatedChars[0].weaponsOf?.length || 0,
          armorOf: elementsWithRelatedChars[0].armorOf?.length || 0
        });
      }
    }
  }, [elements.length, elements]);

  // Debug: Monitor filteredElements changes
  useEffect(() => {
    console.log('🔍 Elements: filteredElements changed:', {
      filteredElementsLength: filteredElements.length,
      searchTerm,
      sortBy
    });
  }, [filteredElements.length, searchTerm, sortBy]);

  useEffect(() => {
    console.log('🔍 Elements: Starting filter/sort effect');
    // Filter and sort elements
    if (Array.isArray(elements)) {
      let filtered = elements.filter(element =>
        element && element.name && element.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Sort the filtered elements
      filtered = filtered.sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return a.name.localeCompare(b.name);
          case 'name-desc':
            return b.name.localeCompare(a.name);
          case 'date-created':
            // Sort by creation date (newest first)
            const dateA = new Date(a.createdAt || a._id);
            const dateB = new Date(b.createdAt || b._id);
            return dateB.getTime() - dateA.getTime();
          case 'date-created-oldest':
            // Sort by creation date (oldest first)
            const dateAOldest = new Date(a.createdAt || a._id);
            const dateBOldest = new Date(b.createdAt || b._id);
            return dateAOldest.getTime() - dateBOldest.getTime();
            
          default:
            return 0;
        }
      });

      console.log('🔍 Elements: Setting filteredElements:', {
        originalLength: elements.length,
        filteredLength: filtered.length
      });
      setFilteredElements(filtered);
    } else {
      console.log('🔍 Elements: elements is not an array, setting empty filteredElements');
      setFilteredElements([]);
    }
  }, [elements, searchTerm, sortBy]);

  const loadElements = async () => {
    try {
      setLoading(true);
      const data = await elementsAPI.getAll();
      console.log('Elements data received:', data);
      
      // Debug: Check if related character data is present
      if (Array.isArray(data) && data.length > 0) {
        const firstElement = data[0];
        console.log('🔍 First element related character data:', {
          name: firstElement.name,
          hasWeaponsOf: !!firstElement.weaponsOf,
          weaponsOfLength: firstElement.weaponsOf?.length || 0,
          hasArmorOf: !!firstElement.armorOf,
          armorOfLength: firstElement.armorOf?.length || 0,
          hasItemsOf: !!firstElement.itemsOf,
          itemsOfLength: firstElement.itemsOf?.length || 0,
          hasSpeciesOf: !!firstElement.speciesOf,
          speciesOfLength: firstElement.speciesOf?.length || 0,
          hasGenderOf: !!firstElement.genderOf,
          genderOfLength: firstElement.genderOf?.length || 0,
          hasBeliefsOf: !!firstElement.beliefsOf,
          beliefsOfLength: firstElement.beliefsOf?.length || 0,
          hasFamilyOf: !!firstElement.familyOf,
          familyOfLength: firstElement.familyOf?.length || 0,
          hasAffiliationsOf: !!firstElement.affiliationsOf,
          affiliationsOfLength: firstElement.affiliationsOf?.length || 0,
          hasAbilitiesOf: !!firstElement.abilitiesOf,
          abilitiesOfLength: firstElement.abilitiesOf?.length || 0
        });
      }
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setElements(data);
        // Increment data version to force remounts when data changes
        setDataVersion(prev => prev + 1);
      } else {
        console.error('API returned non-array data:', data);
        setElements([]);
      }
    } catch (error) {
      console.error('Failed to load elements:', error);
      setElements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleElementCreated = (newElement: Element) => {
    console.log('New element created:', newElement);
    
    // Refresh all elements to ensure hierarchy and relationships are properly updated
    const refreshAllElements = async () => {
      try {
        console.log('🔄 Elements: Refreshing all elements after creation...');
        const refreshedElements = await elementsAPI.getAll();
        setElements(refreshedElements);
        setDataVersion(prev => prev + 1);
        console.log('🔄 Elements: All elements refreshed successfully after creation');
        
        // Show success toast
        if (typeof window.showToast === 'function') {
          window.showToast(`Element "${newElement.name}" created successfully!`, 'success');
        }
      } catch (error) {
        console.error('🔄 Elements: Failed to refresh all elements after creation:', error);
        // Fallback to adding just the new element
        setElements(prev => {
          const updated = [newElement, ...prev];
          console.log('Updated elements array (fallback):', updated);
          return updated;
        });
      }
    };
    
    refreshAllElements();
    setShowCreateModal(false);
  };

  const handleUpdateElement = (updatedElement: Element) => {
    console.log('🔄 Elements: Updating element:', {
      id: updatedElement._id,
      name: updatedElement.name,
      imageUrl: updatedElement.imageUrl?.substring(0, 50) + '...'
    });
    
    // Refresh all elements to ensure linked arrays are current
    const refreshAllElements = async () => {
      try {
        console.log('🔄 Elements: Refreshing all elements after update...');
        const refreshedElements = await elementsAPI.getAll();
        setElements(refreshedElements);
        setDataVersion(prev => prev + 1);
        console.log('🔄 Elements: All elements refreshed successfully');
      } catch (error) {
        console.error('🔄 Elements: Failed to refresh all elements:', error);
        // Fallback to updating just the specific element
        setElements(prev => {
          const updated = prev.map(e => 
            e._id === updatedElement._id ? updatedElement : e
          );
          return updated;
        });
      }
    };
    
    refreshAllElements();
    
    // Track which element was updated for targeted re-rendering
    setUpdatedElementIds(prev => new Set(Array.from(prev).concat(updatedElement._id)));
    
    // Clear the updated flag after a short delay to prevent unnecessary re-renders
    setTimeout(() => {
      setUpdatedElementIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(updatedElement._id);
        return newSet;
      });
    }, 1000);
  };

  const handleDeleteElement = (elementId: string) => {
    setElements(prev => prev.filter(e => e._id !== elementId));
  };

  const handleOpenSidePanel = (element: Element, field: string) => {
    setSelectedElement(element);
    setShowElementPanel(true);
  };

  const handleOpenPreviewPanel = (elementIds: string[]) => {
    // Handle preview panel
    console.log('Opening preview for elements:', elementIds);
  };

  if (loading) {
    return <div className="elements loading">Loading elements...</div>;
  }

  return (
    <div className="elements">
      {/* Header */}
      <div className="elements-header">
        <div>
          <h1 className="elements-title">Elements</h1>
          <p className="elements-subtitle">Manage and organize your elements</p>
        </div>
        <div className="elements-actions">
          <button 
            className="btn btn--green" 
            onClick={() => setShowCreateModal(true)}
          >
            Create New Element
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest')}
            className="sort-select"
          >
            <option value="date-created">Newest First</option>
            <option value="date-created-oldest">Oldest First</option>
            <option value="name-asc">A to Z</option>
            <option value="name-desc">Z to A</option>
          </select>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="live-search"
          />
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        {/* Left side - Filter dropdowns */}
        <div className="filter-left">
          <button
            className="btn btn--clear-filters"
            onClick={() => {
              setSearchTerm('');
            }}
          >
            Clear Filters
          </button>
        </div>

        {/* Right side - Tab selection */}
        <div className="filter-right">
          <div className="filter-group">
            <label htmlFor="tab-filter" className="filter-label">Default Tab:</label>
            <select
              id="tab-filter"
              value={globalTabSelection}
              onChange={(e) => setGlobalTabSelection(e.target.value)}
              className="filter-select tab-select"
            >
              <option value="summary">Summary</option>
              <option value="features">Features</option>
              <option value="attributes">Attributes</option>
              <option value="linkedCharacters">Linked Characters</option>
            </select>
          </div>
        </div>
      </div>

      {/* Element Cards */}
      <div className="card-container" id="results-container">
        {(() => {
          console.log('🔍 Elements: Rendering card container with:', {
            isArray: Array.isArray(filteredElements),
            filteredElementsLength: filteredElements?.length || 0,
            loading
          });
          return null;
        })()}
        {Array.isArray(filteredElements) && filteredElements.length > 0 ? (
          filteredElements.map(element => {
            // Create a hash of related character data to force remount when data changes
            const relatedCharsHash = [
              element.weaponsOf?.length || 0,
              element.armorOf?.length || 0,
              element.itemsOf?.length || 0,
              element.speciesOf?.length || 0,
              element.genderOf?.length || 0,
              element.beliefsOf?.length || 0,
              element.familyOf?.length || 0,
              element.affiliationsOf?.length || 0,
              element.abilitiesOf?.length || 0
            ].join('-');
            
            // Debug: Log when related character data changes
            console.log(`🔍 ElementCard key for ${element.name}:`, {
              elementId: element._id,
              relatedCharsHash,
              hasWeaponsOf: !!element.weaponsOf,
              weaponsOfLength: element.weaponsOf?.length || 0
            });
            
            return (
              <ElementCard
                key={`${element._id}-${dataVersion}-${relatedCharsHash}`}
                element={element}
                onUpdate={handleUpdateElement}
                onDelete={handleDeleteElement}
                onOpenSidePanel={handleOpenSidePanel}
                onOpenPreviewPanel={handleOpenPreviewPanel}
                defaultTab={globalTabSelection}
              />
            );
          })
        ) : (
          <div className="no-results">
            <p>No elements found.</p>
          </div>
        )}
      </div>

      {/* Create Element Modal */}
      {showCreateModal && (
        <CreateElementModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onElementCreated={handleElementCreated}
        />
      )}

      {/* Element Side Panel */}
      {showElementPanel && selectedElement && (
        <ElementSidePanel
          isOpen={showElementPanel}
          element={selectedElement}
          onClose={() => {
            setShowElementPanel(false);
            setSelectedElement(null);
          }}
          onUpdate={handleUpdateElement}
        />
      )}

      {/* Element Preview Panel */}
      {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={() => setShowElementPreview(false)}
        />
      )}
    </div>
  );
};

export default Elements; 